// 事件通知的接口与展示文案（docs/04 第 15 节，决策 D210）：全部接口只给超级管理员（D181）。
import type { Tone } from "@/ui";
import { api } from "./api";
import type { ListCount } from "./usePage";

export type NotifyLevel = "info" | "warning" | "critical";
export type NotifyStatus = "pending" | "sent" | "failed" | "skipped";

/** 等级与状态的胶囊色调和中文标签；页面不自行选色。 */
export const NOTIFY_LEVEL: Record<NotifyLevel, { tone: Tone; label: string }> = {
  info: { tone: "neutral", label: "信息" },
  warning: { tone: "warning", label: "警告" },
  critical: { tone: "danger", label: "严重" },
};
export const NOTIFY_STATUS: Record<NotifyStatus, { tone: Tone; label: string }> = {
  pending: { tone: "brand", label: "待发送" },
  sent: { tone: "success", label: "已发送" },
  failed: { tone: "danger", label: "失败" },
  skipped: { tone: "neutral", label: "已跳过" },
};
export const LEVEL_OPTIONS = (Object.keys(NOTIFY_LEVEL) as NotifyLevel[]).map((l) => ({ value: l, label: NOTIFY_LEVEL[l].label }));

/** 类型前缀的中文名，按页面展示顺序；目录里出现表外的前缀时原样显示、排在最后。 */
export const TYPE_GROUPS: [prefix: string, label: string][] = [
  ["billing", "计费"], ["channel", "通道"], ["account", "客户账号"], ["admin_user", "后台账号"], ["routing", "路由"], ["system", "系统"],
];

export interface NotifySettings { url: string; secret: string; source: string; updated_at: string | null }
/** secret 不给、空串或打码值是不改，null 清除，其余是新密钥（D43）。 */
export interface NotifySettingsInput { url: string; source: string; secret?: string | null }
export interface NotifyTestResult { ok: boolean; status_code: number | null; duration_ms: number; error: string }

export interface NotifyTypeOverride { type: string; enabled: boolean; level: NotifyLevel | null; updated_at: string }
export interface NotifyType {
  type: string; name: string; locked: boolean; default_level: NotifyLevel; default_enabled: boolean;
  /** 合并覆盖后实际生效的值。 */
  enabled: boolean; level: NotifyLevel; override: NotifyTypeOverride | null;
}

export interface NotifyEvent {
  id: number; event_id: string; type: string; level: NotifyLevel; title: string; actor: string; occurred_at: string; status: NotifyStatus;
  attempts: number; next_at: string | null; last_status_code: number | null; skip_reason: string | null; updated_at: string;
}
export interface NotifyEventDetail extends NotifyEvent {
  body: string; object: { kind: string; id: string; name: string } | null; data: unknown; lease_until: string | null; last_error: string | null; sent_payload: unknown;
}
export interface NotifyEventFilter { type?: string; status?: string; from?: string; to?: string; limit?: number; before_id?: number }

const query = (f: NotifyEventFilter) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : "";
};

/** 取某个类型前缀的分组名。 */
export const prefixOf = (type: string) => type.split(".")[0];
export const groupLabel = (prefix: string) => TYPE_GROUPS.find(([p]) => p === prefix)?.[1] ?? prefix;

const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });

export const notify = {
  settings: () => api<NotifySettings>("/notify/settings"),
  saveSettings: (body: NotifySettingsInput) => api<NotifySettings>("/notify/settings", json("PUT", body)),
  /** 用已保存的设置发一个 system.test 事件；接收方失败也回 200，看 ok。 */
  test: () => api<NotifyTestResult>("/notify/settings/test", json("POST")),
  types: () => api<NotifyType[]>("/notify/types"),
  saveType: (type: string, body: { enabled: boolean; level: NotifyLevel | null }) => api<NotifyType>(`/notify/types/${encodeURIComponent(type)}`, json("PUT", body)),
  resetType: (type: string) => api<NotifyType>(`/notify/types/${encodeURIComponent(type)}`, json("DELETE")),
  events: (f: NotifyEventFilter) => api<NotifyEvent[]>(`/notify/events${query(f)}`),
  eventCount: (f: Pick<NotifyEventFilter, "type" | "status" | "from" | "to">) => api<ListCount>(`/notify/events/count${query(f)}`),
  event: (id: number) => api<NotifyEventDetail>(`/notify/events/${id}`),
  resend: (id: number) => api<NotifyEvent>(`/notify/events/${id}/resend`, json("POST")),
};
